<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>订单详情</span>
        <el-button @click="backToList">返回列表</el-button>
      </div>

      <el-alert
        v-if="loadError"
        class="state-block"
        title="订单详情加载失败"
        type="error"
        :closable="false"
        show-icon
      >
        <template slot>
          <span>可以检查订单号是否有效，或稍后重试。</span>
          <el-button type="text" @click="loadDetail">重新加载</el-button>
        </template>
      </el-alert>

      <div v-else v-loading="loading">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="订单号">{{ detail.orderNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">{{ statusText(detail.orderStatus, detail.pickupStatus) }}</el-descriptions-item>
          <el-descriptions-item label="订单类型">
            <el-tag size="mini" :type="Number(detail.orderType) === 1 ? 'warning' : 'info'">
              {{ Number(detail.orderType) === 1 ? '团购单' : '普通单' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="发货方式">
            <el-tag size="mini" :type="isPickupOrder ? 'warning' : 'success'">
              {{ isPickupOrder ? '用户自提' : '物流快递' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="拼团编号">{{ detail.groupBuyRecordId || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="Number(detail.orderType) === 1" label="拼团状态">{{ groupBuyStatusText(detail.groupBuyStatus) }}</el-descriptions-item>
          <el-descriptions-item label="成团来源">{{ groupBuySuccessSourceText(detail.groupBuySuccessSource) }}</el-descriptions-item>
          <el-descriptions-item label="成团备注">{{ detail.groupBuySuccessRemark || '-' }}</el-descriptions-item>
          <el-descriptions-item label="支付状态">{{ Number(detail.payStatus) === 1 ? '已支付' : '未支付' }}</el-descriptions-item>
          <el-descriptions-item label="发货状态">{{ deliveryStatusText }}</el-descriptions-item>
          <el-descriptions-item label="退款状态">
            <span v-if="!detail.refundStatus">无退款</span>
            <el-tag v-else :type="refundTagType(detail.refundStatus)">{{ refundText(detail.refundStatus) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="退款单号">{{ detail.refundNo || '-' }}</el-descriptions-item>
          <el-descriptions-item label="关闭原因">{{ closeReasonText(detail.closeReason) }}</el-descriptions-item>
          <el-descriptions-item label="关闭备注">{{ detail.closeRemark || '-' }}</el-descriptions-item>
          <el-descriptions-item label="收货人">{{ detail.receiverName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ detail.receiverMobile || '-' }}</el-descriptions-item>
          <el-descriptions-item label="实付金额">￥{{ formatPrice(detail.payAmount) }}</el-descriptions-item>
          <el-descriptions-item label="商品金额">￥{{ formatPrice(detail.totalAmount) }}</el-descriptions-item>
          <el-descriptions-item label="运费">￥{{ formatPrice(detail.freightAmount) }}</el-descriptions-item>
          <el-descriptions-item label="下单时间">{{ formatDateTime(detail.createTime) }}</el-descriptions-item>
          <el-descriptions-item label="支付时间">{{ formatDateTime(detail.paymentTime) }}</el-descriptions-item>
          <el-descriptions-item label="发货时间">{{ formatDateTime(detail.deliveryTime) }}</el-descriptions-item>
          <el-descriptions-item label="收货时间">{{ formatDateTime(detail.receiveTime) }}</el-descriptions-item>
          <el-descriptions-item label="退款时间">{{ formatDateTime(detail.refundTime) }}</el-descriptions-item>
          <el-descriptions-item v-if="!isPickupOrder" label="收货地址" :span="2">{{ fullAddress }}</el-descriptions-item>
          <el-descriptions-item v-if="detail.buyerRemark" label="买家备注" :span="2">{{ detail.buyerRemark }}</el-descriptions-item>
          <el-descriptions-item v-if="detail.expressCompany" label="物流信息" :span="2">
            {{ detail.expressCompany }} {{ detail.expressNo }}
            <el-button v-if="detail.expressNo" type="text" @click="copyText(detail.expressNo, '物流单号已复制')">复制单号</el-button>
          </el-descriptions-item>

          <el-descriptions-item v-if="isPickupOrder" label="自提点" :span="2">{{ detail.pickupStoreName || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="isPickupOrder" label="自提地址" :span="2">{{ detail.pickupStoreAddress || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="isPickupOrder" label="自提联系人">{{ detail.pickupContact || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="isPickupOrder" label="联系电话">{{ detail.pickupMobile || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="isPickupOrder" label="提货码">
            {{ detail.pickupCode || '-' }}
            <el-button v-if="detail.pickupCode" type="text" @click="copyText(detail.pickupCode, '提货码已复制')">复制提货码</el-button>
          </el-descriptions-item>
          <el-descriptions-item v-if="isPickupOrder" label="自提状态">{{ pickupStatusText(detail.pickupStatus) }}</el-descriptions-item>
          <el-descriptions-item v-if="isPickupOrder" label="备货完成时间">{{ formatDateTime(detail.pickupReadyTime) }}</el-descriptions-item>
          <el-descriptions-item v-if="isPickupOrder && detail.pickupStoreBusinessHours" label="营业时间">{{ detail.pickupStoreBusinessHours }}</el-descriptions-item>
          <el-descriptions-item v-if="isPickupOrder" label="提货完成时间">{{ formatDateTime(detail.pickupTime) }}</el-descriptions-item>

          <el-descriptions-item label="售后状态">{{ afterSaleText(detail.afterSaleStatus) }}</el-descriptions-item>
          <el-descriptions-item label="售后申请时间">{{ formatDateTime(detail.afterSaleApplyTime) }}</el-descriptions-item>
          <el-descriptions-item v-if="detail.afterSaleReason" label="售后原因" :span="2">{{ detail.afterSaleReason }}</el-descriptions-item>
          <el-descriptions-item v-if="detail.afterSaleRemark" label="处理备注" :span="2">{{ detail.afterSaleRemark }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-card>

    <el-card v-if="!loadError" shadow="never" class="section-card">
      <div slot="header">商品明细</div>
      <el-table :data="detail.items || []" border v-loading="loading">
        <el-table-column prop="productName" label="商品名称" />
        <el-table-column prop="skuName" label="SKU" />
        <el-table-column label="单价" width="120">
          <template slot-scope="{ row }">￥{{ formatPrice(row.price) }}</template>
        </el-table-column>
        <el-table-column prop="quantity" label="数量" width="100" />
      </el-table>
    </el-card>

    <el-card v-if="!loadError && showDeliveryPanel" shadow="never" class="section-card">
      <div slot="header">订单发货</div>
      <el-alert
        v-if="!canDelivery"
        type="warning"
        :closable="false"
        show-icon
        class="state-block"
        title="该团购订单尚未拼团成功，暂时不能发货。请先完成成团，或超时后标记失败退款。"
      />
      <template v-else>
        <el-form label-width="100px">
          <template v-if="isPickupOrder">
            <el-alert
              type="info"
              :closable="false"
              show-icon
              title="自提订单会自动使用商品绑定的自提点和系统生成的提货码。确认备货完成后，前端订单会进入“待自提”。"
            />
            <el-form-item label="自提点">{{ detail.pickupStoreName || '-' }}</el-form-item>
            <el-form-item label="提货码">{{ detail.pickupCode || '-' }}</el-form-item>
          </template>
          <template v-else>
            <el-form-item label="快递公司">
              <el-select
                v-model="delivery.expressCompany"
                filterable
                clearable
                :filter-method="filterDeliveryCompanies"
                :loading="deliveryCompanyLoading"
                placeholder="请选择发货快递"
                style="width: 100%"
              >
                <el-option
                  v-for="item in visibleDeliveryCompanyOptions"
                  :key="item.code || item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
                <div class="delivery-company-actions">
                  <span class="delivery-company-actions__hint">
                  {{ deliveryCompanyKeyword ? `搜索结果 ${visibleDeliveryCompanyOptions.length} 项` : `默认展示常用快递 ${visibleDeliveryCompanyOptions.length} 项` }}
                  </span>
                  <el-button
                  v-if="!deliveryCompanyKeyword"
                    type="text"
                    @click="toggleAllDeliveryCompanies"
                  >
                  {{ showAllDeliveryCompanies ? '收起到常用快递' : '显示全部物流公司' }}
                  </el-button>
                </div>
            </el-form-item>
            <el-form-item label="物流单号"><el-input v-model.trim="delivery.expressNo" /></el-form-item>
          </template>
          <el-form-item label="备注"><el-input v-model.trim="delivery.remark" /></el-form-item>
        </el-form>
        <el-button
          v-if="canPreparePickup || !isPickupOrder"
          type="primary"
          :loading="deliverySubmitting"
          @click="submitDelivery"
        >
          {{ isPickupOrder ? '确认备货完成' : '确认发货' }}
        </el-button>
        <el-button
          v-if="canFinishPickup"
          style="margin-left: 12px"
          :loading="pickupSubmitting"
          @click="finishPickup"
        >
          确认提货完成
        </el-button>
      </template>
    </el-card>

    <el-card v-if="!loadError && detail.afterSaleStatus" shadow="never" class="section-card">
      <div slot="header">售后处理</div>
      <el-form label-width="100px">
        <el-form-item label="处理状态">
          <el-radio-group v-model="afterSaleForm.afterSaleStatus">
            <el-radio :label="1">已申请</el-radio>
            <el-radio :label="2">处理中</el-radio>
            <el-radio :label="3">已处理</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="处理备注">
          <el-input v-model.trim="afterSaleForm.afterSaleRemark" type="textarea" :rows="3" maxlength="255" show-word-limit />
        </el-form-item>
      </el-form>
      <el-button type="primary" :loading="afterSaleSubmitting" @click="submitAfterSale">保存售后处理</el-button>
    </el-card>

    <el-card v-if="!loadError && canShowRefundPanel" shadow="never" class="section-card">
      <div slot="header">退款处理</div>
      <el-alert v-if="detail.refundNo" type="info" :closable="false" show-icon class="state-block">
        <template slot>
          <span>当前退款单号：{{ detail.refundNo }}，状态：{{ refundText(detail.refundStatus) }}</span>
        </template>
      </el-alert>
      <el-alert
        v-if="Number(detail.refundStatus) === 2"
        type="success"
        :closable="false"
        show-icon
        class="state-block"
        title="该订单已退款成功，不能重复创建退款单或修改退款结果。"
      />
      <el-alert
        v-else-if="Number(detail.refundRecordStatus) === 1"
        type="warning"
        :closable="false"
        show-icon
        class="state-block"
        title="退款已进入处理中，请等待微信退款回调；如线下已处理，可人工标记结果。"
      />
      <el-form label-width="100px">
        <el-form-item label="退款原因">
          <el-input
            v-model.trim="refundForm.reason"
            type="textarea"
            :rows="3"
            maxlength="255"
            show-word-limit
            placeholder="例如：用户申请售后退款"
          />
        </el-form-item>
      </el-form>
      <el-button v-if="canCreateRefund" type="danger" :loading="refundSubmitting" @click="createRefund">
        {{ Number(detail.refundRecordStatus) === 3 ? '重新创建退款单' : '创建退款单' }}
      </el-button>
      <el-button v-if="canProcessRefund" type="danger" :loading="refundSubmitting" @click="processRefund">发起微信退款</el-button>
      <el-button v-if="canMarkRefundResult" :loading="refundSubmitting" @click="markRefundSuccess">标记退款成功</el-button>
      <el-button v-if="canMarkRefundResult" :loading="refundSubmitting" @click="markRefundFail">标记退款失败</el-button>
    </el-card>

    <el-card v-if="!loadError && showWxShippingPanel" shadow="never" class="section-card">
      <div slot="header" class="sync-header">
        <div class="sync-header__title">
          <span>微信发货同步</span>
          <el-tag
            size="mini"
            :type="hasWxShippingSync ? wxShippingStatusType(detail.wxShippingSyncStatus) : 'info'"
          >
            {{ hasWxShippingSync ? wxShippingStatusText(detail.wxShippingSyncStatus) : '待触发' }}
          </el-tag>
        </div>
        <div class="sync-header__actions">
          <el-button v-if="canRetryWxShipping" type="text" @click="retryWxShipping">重试同步</el-button>
          <el-button type="text" @click="wxShippingExpanded = !wxShippingExpanded">
            {{ wxShippingExpanded ? '收起' : '展开' }}
          </el-button>
        </div>
      </div>
      <el-collapse-transition>
        <div v-show="wxShippingExpanded">
          <el-alert
            v-if="!hasWxShippingSync"
            type="info"
            :closable="false"
            show-icon
            :title="wxShippingPlaceholder()"
          />
          <el-descriptions v-else :column="2" border>
            <el-descriptions-item label="同步状态">
              <el-tag :type="wxShippingStatusType(detail.wxShippingSyncStatus)">
                {{ wxShippingStatusText(detail.wxShippingSyncStatus) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="触发时机">{{ wxShippingTriggerText(detail.wxShippingTriggerType) }}</el-descriptions-item>
            <el-descriptions-item label="同步时间">{{ formatDateTime(detail.wxShippingSyncTime) }}</el-descriptions-item>
            <el-descriptions-item label="重试次数">{{ detail.wxShippingRetryCount || 0 }}</el-descriptions-item>
            <el-descriptions-item label="微信订单状态">{{ detail.wxShippingOrderState || '-' }}</el-descriptions-item>
            <el-descriptions-item label="同步记录ID">{{ detail.wxShippingSyncId || '-' }}</el-descriptions-item>
            <el-descriptions-item v-if="detail.wxShippingLastError" label="最后错误" :span="2">
              <div class="code-block">{{ detail.wxShippingLastError }}</div>
            </el-descriptions-item>
            <el-descriptions-item v-if="detail.wxShippingRequestBody" label="请求报文" :span="2">
              <div class="code-block">{{ detail.wxShippingRequestBody }}</div>
            </el-descriptions-item>
            <el-descriptions-item v-if="detail.wxShippingResponseBody" label="返回报文" :span="2">
              <div class="code-block">{{ detail.wxShippingResponseBody }}</div>
            </el-descriptions-item>
          </el-descriptions>
        </div>
      </el-collapse-transition>
    </el-card>
  </div>
</template>

<script>
import {
  createOrderRefund,
  deliveryOrder,
  finishPickupOrder,
  getOrderDetail,
  getWechatDeliveryCompanies,
  handleOrderAfterSale,
  markOrderRefundFail,
  markOrderRefundSuccess,
  processOrderRefund,
  retryWechatShippingSync
} from '../../api/admin'
import { formatDateTime, formatPrice } from '../../utils/format'

export default {
  props: {
    orderNo: { type: String, required: true }
  },
  data() {
    return {
      loading: false,
      loadError: false,
      deliverySubmitting: false,
      pickupSubmitting: false,
      afterSaleSubmitting: false,
      refundSubmitting: false,
      deliveryCompanyLoading: false,
      detail: { items: [] },
      deliveryCompanyOptions: [],
      deliveryCompanyFullLoaded: false,
      deliveryCompanyKeyword: '',
      showAllDeliveryCompanies: false,
      wxShippingExpanded: false,
      delivery: {
        expressCompany: '',
        expressNo: '',
        pickupContact: '',
        pickupMobile: '',
        pickupStoreName: '',
        pickupStoreAddress: '',
        pickupCode: '',
        remark: ''
      },
      afterSaleForm: { afterSaleStatus: 1, afterSaleRemark: '' },
      refundForm: { reason: '' }
    }
  },
  computed: {
    fullAddress() {
      const parts = [this.detail.receiverProvince, this.detail.receiverCity, this.detail.receiverDistrict, this.detail.receiverAddress].filter(Boolean)
      return parts.length ? parts.join('') : '-'
    },
    isPickupOrder() {
      return Number(this.detail.deliveryType) === 2
    },
    showDeliveryPanel() {
      return Number(this.detail.orderStatus) === 1 && Number(this.detail.payStatus) === 1
    },
    deliveryStatusText() {
      if (this.isPickupOrder) {
        return this.pickupStatusText(this.detail.pickupStatus)
      }
      return Number(this.detail.deliveryStatus || 0) === 1 ? '已发货' : '未发货'
    },
    canDelivery() {
      return Number(this.detail.orderType) !== 1 || Number(this.detail.groupBuyStatus) === 1
    },
    canPreparePickup() {
      return this.isPickupOrder
        && Number(this.detail.orderStatus) === 1
        && Number(this.detail.pickupStatus || 0) === 0
    },
    canFinishPickup() {
      return this.isPickupOrder
        && Number(this.detail.orderStatus) === 2
        && Number(this.detail.pickupStatus || 0) === 1
    },
    canShowRefundPanel() {
      return Number(this.detail.payStatus) === 1 && Number(this.detail.orderStatus) !== 4
    },
    showWxShippingPanel() {
      return Number(this.detail.payStatus) === 1
    },
    hasWxShippingSync() {
      return !!this.detail.wxShippingSyncId
    },
    canRetryWxShipping() {
      return !!this.detail.wxShippingSyncId && [2, 3].includes(Number(this.detail.wxShippingSyncStatus))
    },
    visibleDeliveryCompanyOptions() {
      const keyword = (this.deliveryCompanyKeyword || '').trim().toLowerCase()
      if (keyword) {
        return this.deliveryCompanyOptions.filter(item =>
          (item.label || '').toLowerCase().includes(keyword) ||
          (item.name || '').toLowerCase().includes(keyword) ||
          (item.code || '').toLowerCase().includes(keyword)
        )
      }
      if (this.showAllDeliveryCompanies) {
        return this.deliveryCompanyOptions
      }
      return this.deliveryCompanyOptions
    },
    canCreateRefund() {
      if (!this.canShowRefundPanel || Number(this.detail.refundStatus) === 2) {
        return false
      }
      return !this.detail.refundRecordId || Number(this.detail.refundRecordStatus) === 3
    },
    canProcessRefund() {
      return this.canShowRefundPanel
        && this.detail.refundRecordId
        && Number(this.detail.refundRecordStatus) === 0
    },
    canMarkRefundResult() {
      return this.canShowRefundPanel
        && this.detail.refundRecordId
        && [0, 1].includes(Number(this.detail.refundRecordStatus))
    }
  },
  created() {
    this.loadDetail()
      this.loadDeliveryCompanies(false)
  },
  methods: {
    formatDateTime,
    formatPrice,
    statusText(status, pickupStatus) {
      if (this.isPickupOrder) {
        if (Number(status) === 1 && Number(pickupStatus || 0) === 0) {
          return '待备货'
        }
        if (Number(status) === 2) {
          return '待自提'
        }
      }
      const map = { 0: '待付款', 1: '待发货', 2: '待收货', 3: '已完成', 4: '已取消' }
      return map[Number(status)] || '未知状态'
    },
    pickupStatusText(status) {
      const map = { 0: '待备货', 1: '待自提', 2: '已提货' }
      return map[Number(status || 0)] || '待备货'
    },
    wxShippingStatusText(status) {
      const map = { 0: '待同步', 1: '同步成功', 2: '失败待重试', 3: '需人工处理' }
      return map[Number(status)] || '待同步'
    },
    wxShippingStatusType(status) {
      const map = { 0: 'info', 1: 'success', 2: 'warning', 3: 'danger' }
      return map[Number(status)] || 'info'
    },
    wxShippingTriggerText(triggerType) {
      const map = { 1: '快递发货触发', 2: '自提备货触发' }
      return map[Number(triggerType)] || '发货事件'
    },
    wxShippingPlaceholder() {
      if (Number(this.detail.payStatus) !== 1) {
        return '订单未支付，暂不需要同步微信发货信息。'
      }
      if (this.isPickupOrder && Number(this.detail.pickupStatus || 0) === 0) {
        return '当前自提订单尚未确认备货完成，微信发货信息会在备货完成后自动同步。'
      }
      if (!this.isPickupOrder && Number(this.detail.orderStatus) === 1) {
        return '当前订单尚未发货，微信发货信息会在后台确认发货后自动同步。'
      }
      return '当前订单暂无微信发货同步记录。'
    },
    refundText(status) {
      const map = { 1: '退款中', 2: '退款成功', 3: '退款失败' }
      return map[Number(status)] || '无退款'
    },
    refundTagType(status) {
      const map = { 1: 'warning', 2: 'success', 3: 'danger' }
      return map[Number(status)] || 'info'
    },
    afterSaleText(status) {
      const map = { 0: '无售后', 1: '已申请', 2: '处理中', 3: '已处理' }
      return map[Number(status || 0)] || '无售后'
    },
    groupBuySuccessSourceText(source) {
      const map = {
        NATURAL: '自然成团',
        ADMIN_COMPLETE: '后台提前成团',
        ADMIN_FILL: '后台补满成团'
      }
      return map[source] || '-'
    },
    groupBuyStatusText(status) {
      const map = { 0: '拼团中', 1: '已成团', 2: '已关闭' }
      return map[Number(status)] || '-'
    },
    closeReasonText(reason) {
      const map = {
        USER_CANCEL: '用户取消',
        TIMEOUT: '超时关闭',
        GROUP_BUY_FAIL: '拼团失败',
        REFUND_SUCCESS: '退款关闭'
      }
      return map[reason] || reason || '-'
    },
    async loadDetail() {
      this.loading = true
      this.loadError = false
      try {
        this.detail = await getOrderDetail(this.orderNo)
        this.afterSaleForm = {
          afterSaleStatus: this.detail.afterSaleStatus || 1,
          afterSaleRemark: this.detail.afterSaleRemark || ''
        }
        this.delivery = {
          expressCompany: this.detail.expressCompany || '',
          expressNo: this.detail.expressNo || '',
          pickupContact: this.detail.pickupContact || '',
          pickupMobile: this.detail.pickupMobile || '',
          pickupStoreName: this.detail.pickupStoreName || '',
          pickupStoreAddress: this.detail.pickupStoreAddress || '',
          pickupCode: this.detail.pickupCode || '',
          remark: ''
        }
        this.refundForm.reason = this.detail.afterSaleReason || this.detail.closeRemark || '后台发起普通订单退款'
      } catch (error) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    async loadDeliveryCompanies(full = false) {
      this.deliveryCompanyLoading = true
      try {
        const list = await getWechatDeliveryCompanies({ full })
        this.deliveryCompanyOptions = (list || []).map(item => ({
          code: item.code,
          name: item.name,
          value: item.name || item.code,
          label: item.name && item.code ? `${item.name}（${item.code}）` : (item.name || item.code || '')
        })).filter(item => item.value)
        this.deliveryCompanyFullLoaded = full
        this.deliveryCompanyKeyword = ''
        this.showAllDeliveryCompanies = false
      } finally {
        this.deliveryCompanyLoading = false
      }
    },
    async ensureFullDeliveryCompaniesLoaded() {
      if (this.deliveryCompanyFullLoaded || this.deliveryCompanyLoading) {
        return
      }
      await this.loadDeliveryCompanies(true)
    },
    filterDeliveryCompanies(keyword) {
      this.deliveryCompanyKeyword = keyword || ''
      if (this.deliveryCompanyKeyword) {
        this.showAllDeliveryCompanies = true
        this.ensureFullDeliveryCompaniesLoaded()
      }
    },
    async toggleAllDeliveryCompanies() {
      if (!this.showAllDeliveryCompanies) {
        await this.ensureFullDeliveryCompaniesLoaded()
        this.showAllDeliveryCompanies = true
        return
      }
      this.showAllDeliveryCompanies = false
    },
    async submitDelivery() {
      this.deliverySubmitting = true
      try {
        await deliveryOrder({
          orderNo: this.orderNo,
          deliveryType: this.detail.deliveryType || 1,
          expressCompany: this.delivery.expressCompany,
          expressNo: this.delivery.expressNo,
          pickupContact: this.delivery.pickupContact,
          pickupMobile: this.delivery.pickupMobile,
          pickupStoreName: this.delivery.pickupStoreName,
          pickupStoreAddress: this.delivery.pickupStoreAddress,
          pickupCode: this.delivery.pickupCode,
          remark: this.delivery.remark
        })
        this.$message.success(this.isPickupOrder ? '已确认备货完成' : '发货成功')
        this.backToList()
      } finally {
        this.deliverySubmitting = false
      }
    },
    async finishPickup() {
      this.pickupSubmitting = true
      try {
        await finishPickupOrder(this.orderNo)
        this.$message.success('已确认提货完成')
        this.backToList()
      } finally {
        this.pickupSubmitting = false
      }
    },
    async submitAfterSale() {
      this.afterSaleSubmitting = true
      try {
        await handleOrderAfterSale(this.orderNo, this.afterSaleForm)
        this.$message.success('售后处理已保存')
        await this.loadDetail()
      } finally {
        this.afterSaleSubmitting = false
      }
    },
    createRefund() {
      this.$confirm(`确认创建退款单吗？退款金额将按订单实付金额 ￥${this.formatPrice(this.detail.payAmount)} 处理。`, '创建退款单', {
        type: 'warning'
      }).then(async () => {
        this.refundSubmitting = true
        try {
          const data = await createOrderRefund(this.orderNo, { reason: this.refundForm.reason })
          this.$message.success(`退款单已创建：${data.refundNo}`)
          await this.loadDetail()
        } finally {
          this.refundSubmitting = false
        }
      }).catch(() => {})
    },
    processRefund() {
      this.$confirm(`确认发起退款单 ${this.detail.refundNo} 吗？`, '发起微信退款', {
        type: 'warning'
      }).then(async () => {
        this.refundSubmitting = true
        try {
          await processOrderRefund(this.detail.refundRecordId)
          this.$message.success('退款已提交处理')
          await this.loadDetail()
        } finally {
          this.refundSubmitting = false
        }
      }).catch(() => {})
    },
    markRefundSuccess() {
      this.$prompt('可选填写备注', '标记退款成功', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPlaceholder: '例如：线下已退款'
      }).then(async ({ value }) => {
        this.refundSubmitting = true
        try {
          await markOrderRefundSuccess(this.detail.refundRecordId, { remark: value || '' })
          this.$message.success('已标记为退款成功')
          await this.loadDetail()
        } finally {
          this.refundSubmitting = false
        }
      }).catch(() => {})
    },
    markRefundFail() {
      this.$prompt('请填写失败原因', '标记退款失败', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPlaceholder: '例如：退款配置未完成'
      }).then(async ({ value }) => {
        this.refundSubmitting = true
        try {
          await markOrderRefundFail(this.detail.refundRecordId, { remark: value || '' })
          this.$message.success('已标记为退款失败')
          await this.loadDetail()
        } finally {
          this.refundSubmitting = false
        }
      }).catch(() => {})
    },
    retryWxShipping() {
      this.$confirm(`确认重试订单 ${this.orderNo} 的微信发货同步吗？`, '重试微信发货同步', {
        type: 'warning'
      }).then(async () => {
        await retryWechatShippingSync(this.detail.wxShippingSyncId)
        this.$message.success('已提交重试')
        await this.loadDetail()
      }).catch(() => {})
    },
    async copyText(text, successText) {
      if (!text) {
        return
      }
      try {
        await navigator.clipboard.writeText(text)
        this.$message.success(successText)
      } catch (error) {
        this.$message.warning('复制失败，请手动复制')
      }
    },
    backToList() {
      this.$router.push({ path: '/orders', query: this.$route.query.q ? { q: this.$route.query.q } : {} })
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-card {
  margin-top: 20px;
}

.state-block {
  margin-bottom: 16px;
}

.sync-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sync-header__title,
.sync-header__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.code-block {
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.6;
  font-size: 12px;
  color: #5c4b3f;
  background: #f7f4ef;
  border-radius: 10px;
  padding: 10px 12px;
}

.delivery-company-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 24px;
}

.delivery-company-actions__hint {
  color: #8b7a68;
  font-size: 12px;
}
</style>
